import { makeStyles } from '@material-ui/core/styles';
import React, { useContext, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import Dialog from 'components/Dialog';
import ConfirmationPopInContent from 'components/Generic/ConfirmationPopInContent';
import Loading from 'components/Loading';
import { notify } from 'components/Notifier';
import { useClosureById } from 'hooks';
import ClosureSuccess from 'pages/Closures/ClosureSuccess';
import { closeClosure, downloadCumulatedMonhlyReport } from 'services/apiOperations';
import { ClosureForm, ClosureValidateViolation, DialogWidth } from 'types';
import ClosureError from 'pages/Closures/ClosureError';
import { formatMonthForDisplay } from 'utils/date';
import ClosureWarning from 'pages/Closures/ClosureWarning';
import { ClosureContext } from 'pages/Closures/ClosureClose';
import { AppContext } from 'context';
import { FormStepComponentProps } from '../MultiStepForm';
import IncorrectWastage from './IncorrectWastage';
import { ErrorLink } from './ErrorLink';

const useStyles = makeStyles({
  form: {
    display: 'flex',
    flexFlow: 'column',
    alignItems: 'center',
    flex: 1,
  },
});

export enum ErrorPath {
  CharacterizationMissing = 'missingCharacterization',
  IncorrectMonthlyWastage = 'monthlyWastage',
  TransitLineAndProductionNotBalanced = 'transitLineProductionBalance',
  IncorrectAnnualWastage = 'finalAnnualWastagePercent',
  MonthlyWastageCalculation = 'monthlyWastageCalculation',
}

export enum ErrorMessage {
  NegativeWastage = 'negativeWastage',
  WastageLessThanAnnualWastage = 'monthlyWastageLessThanProvisionalWastage',
  NegativeAnnualWastage = 'negativeAnnualWastage',
  MonthlyWastageCalculationError = 'MONTHLY_WASTAGE_DIVISION_BY_ZERO',
}

type Violations = {
  violations?: ClosureValidateViolation[];
  warnings?: ClosureValidateViolation[];
};

const Step3: React.FC<FormStepComponentProps<ClosureForm, ClosureContext>> = ({
  onSubmit,
  formName,
  previousStepData,
  setNextButtonDisabled,
  formContext,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { t: pt } = useTranslation('translation', { keyPrefix: 'closureCloseForm' });

  const [confirmationPopInDisplay, setConfirmationPopInDisplay] = useState(false);
  const [closurePending, setClosurePending] = useState(false);
  const [hasValidated, setHasValidated] = useState(false);
  const [isOnError, setIsOnError] = useState(false);
  const closureId = previousStepData.closureId ?? '';
  const [closure] = useClosureById(closureId);
  const [formViolations, setFormViolations] = useState<Violations | undefined>(formContext);
  const context = useContext(AppContext);
  const sortingCenter = context.getSortingCenter?.();

  // J'aurais pu directemement utiliser formViolations dans le warningComponent
  // mais j'ai preferé copier le tableau pour eviter de cassé l'existant
  if (formViolations?.warnings && formContext?.warnings) {
    if (formViolations.warnings.length > 0 && formContext.warnings.length === 0) {
      formContext.warnings = formViolations.warnings;
    }
  }

  const { handleSubmit } = useForm<ClosureForm>({
    mode: 'onBlur',
  });

  if (!closure) {
    return <Loading />;
  }

  const openConfirmationOverlay: SubmitHandler<ClosureForm> = () => {
    if (hasValidated) {
      setConfirmationPopInDisplay(true);
      return;
    }
    if (!formViolations?.violations || formViolations.violations.length === 0) {
      setIsOnError(true);
    }
  };

  const handleValidation = async () => {
    if (!closure) {
      return;
    }

    setClosurePending(true);
    try {
      await closeClosure(closure.id);
      setConfirmationPopInDisplay(false);
      handleSubmit(onSubmit)();
    } catch (e) {
      notify({
        message: t('closures.close.error'),
        severity: 'error',
      });
    }
    setClosurePending(false);
  };

  const filterViolationsByPath = (violations: ClosureValidateViolation[] | undefined, propertyPath: string) => {
    return violations?.filter((violation: ClosureValidateViolation) => violation.propertyPath === propertyPath);
  };

  const filterViolationsByMessage = (violations: ClosureValidateViolation[] | undefined, message: string) => {
    return violations?.filter((violation: ClosureValidateViolation) => violation.message === message);
  };

  const violationsOrder = [
    {
      violations: filterViolationsByPath(formViolations?.violations, ErrorPath.CharacterizationMissing),
      component: (
        <ClosureError
          {...{
            key: 'noCharacterizationViolation',
            title: pt('error.noCharacterizationErrorTitle'),
          }}
          errorsList={filterViolationsByPath(formViolations?.violations, ErrorPath.CharacterizationMissing)}
        />
      ),
    },
    {
      violations: filterViolationsByMessage(
        filterViolationsByPath(formViolations?.violations, ErrorPath.IncorrectMonthlyWastage),
        ErrorMessage.NegativeWastage,
      ),
      component: (
        <ClosureError
          {...{
            key: 'negativeWastagesViolation',
            title: pt('error.negativeWastageErrorTitle', {
              month: formatMonthForDisplay(closure.month, closure.year),
            }),
          }}
        />
      ),
    },
    {
      violations: filterViolationsByMessage(
        filterViolationsByPath(formViolations?.violations, ErrorPath.IncorrectMonthlyWastage),
        ErrorMessage.WastageLessThanAnnualWastage,
      ),
      component: <IncorrectWastage closure={closure} setViolations={setFormViolations} />,
    },
    {
      violations: filterViolationsByPath(formViolations?.violations, ErrorPath.TransitLineAndProductionNotBalanced),
      component: (
        <ClosureError
          {...{
            key: 'transitLineProductionBalanceViolation',
            title: pt('error.transitLineProductionBalanceTitle'),
          }}
        />
      ),
    },
    {
      violations: filterViolationsByMessage(
        filterViolationsByPath(formViolations?.violations, ErrorPath.IncorrectAnnualWastage),
        ErrorMessage.NegativeAnnualWastage,
      ),
      component: (
        <ClosureError
          {...{
            key: 'negativeAnnualWastagesViolation',
            title: pt('error.negativeAnnualWastageErrorTitle', {
              value: closure?.data?.finalAnnualWastagePercent?.toFixed(4),
            }),
          }}
        />
      ),
    },
    {
      violations: filterViolationsByMessage(
        filterViolationsByPath(formViolations?.violations, ErrorMessage.MonthlyWastageCalculationError),
        ErrorMessage.MonthlyWastageCalculationError,
      ),
      component: (
        <ClosureError
          {...{
            key: 'monthlyWastageCalculationError',
            title: pt('error.monthlyWastageCalculationErrorTitle', {
              month: formatMonthForDisplay(closure.month, closure.year),
            }),
          }}
        />
      ),
    },
  ];

  const violationToDisplay = violationsOrder.find(({ violations }) => !!violations && violations.length > 0);

  setNextButtonDisabled?.(!!violationToDisplay);

  return (
    <>
      <form id={formName} onSubmit={handleSubmit(openConfirmationOverlay)} className={classes.form}>
        <Dialog
          closable={false}
          renderButton={() => {}}
          isOpen={confirmationPopInDisplay}
          onClose={() => setConfirmationPopInDisplay(false)}
          width={DialogWidth.SMALL_FORM}
          renderContent={() =>
            closurePending ? (
              <Loading />
            ) : (
              <ConfirmationPopInContent
                title={t('closures.closureConfirmationPopIn.title')}
                cancelText={t('no')}
                validationText={t('yes')}
                cancelAction={() => setConfirmationPopInDisplay(false)}
                validateAction={handleValidation}
              />
            )
          }
        />
        {violationToDisplay ? (
          <>
            {violationToDisplay.component}
            {closure &&
              sortingCenter &&
              !violationToDisplay.violations?.some(
                violation => violation.message === ErrorMessage.MonthlyWastageCalculationError,
              ) && (
                <ErrorLink
                  prelinkText={pt('step3.checkReport')}
                  linkText={pt('step3.cumulatedReport')}
                  onClick={() =>
                    downloadCumulatedMonhlyReport(
                      sortingCenter['@id'],
                      closure.year.toString(),
                      closure.month.toString(),
                      true,
                    )
                  }
                />
              )}
          </>
        ) : (
          <ClosureSuccess
            key={'noClosureViolation'}
            isPreview
            onConfirm={(_, checked: boolean) => {
              setIsOnError(false);
              setHasValidated(checked);
            }}
            isOnError={isOnError}
            warningComponent={
              !!formContext?.warnings &&
              formContext?.warnings?.length > 0 && (
                <ClosureWarning
                  {...{
                    key: 'unbalancedContracts',
                    title: pt('warning.unbalancedContractsTitle', {
                      month: formatMonthForDisplay(closure.month, closure.year),
                    }),
                  }}
                  warningsList={formContext?.warnings}
                />
              )
            }
          />
        )}
      </form>
    </>
  );
};

export default Step3;
